class DaycareDeliveryComponent extends HTMLElement {
  constructor() {
    super();
    
    // Component state
    this.daycareList = [];
    this.isInitialized = false;
    
    // API configuration
    this.STOREFRONT_API_URL = 'https://woosh-diapers.myshopify.com/api/2024-04/graphql.json';
    this.STOREFRONT_API_TOKEN = '562f5f07859bdef42ffd3ace2e86867c';
    
    // Bind methods
    this.handleDeliveryTypeChange = this.handleDeliveryTypeChange.bind(this);
    this.handleCheckoutClick = this.handleCheckoutClick.bind(this);
    this.handleDaycareInput = this.handleDaycareInput.bind(this);
    this.handleSuggestionClick = this.handleSuggestionClick.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handlePageShow = this.handlePageShow.bind(this);
    this.handleCartOpen = this.handleCartOpen.bind(this);
  }

  static get observedAttributes() {
    return ['api-url', 'api-token'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'api-url' && newValue) {
      this.STOREFRONT_API_URL = newValue;
    }
    if (name === 'api-token' && newValue) {
      this.STOREFRONT_API_TOKEN = newValue;
    }
  }

  connectedCallback() {
    this.setupEventListeners();
    this.disableCheckoutIfNoSelection();
    this.init();
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  setupEventListeners() {
    // Delivery type change - event delegation
    document.addEventListener('change', this.handleDeliveryTypeChange);
    
    // Daycare input - event delegation
    document.addEventListener('input', this.handleDaycareInput);
    
    // Global event listeners
    document.addEventListener('cart:updated', this.handleCartUpdate);
    document.addEventListener('cart:open', this.handleCartOpen);
    document.addEventListener('cart-drawer:open', this.handleCartOpen);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('load', this.init.bind(this));
    window.addEventListener('pageshow', this.handlePageShow);

    
    // Setup checkout button listener
    this.setupCheckoutButtonListener();
  }

  removeEventListeners() {
    document.removeEventListener('change', this.handleDeliveryTypeChange);
    document.removeEventListener('input', this.handleDaycareInput);
    document.removeEventListener('cart:updated', this.handleCartUpdate);
    document.removeEventListener('cart:open', this.handleCartOpen);
    document.removeEventListener('cart-drawer:open', this.handleCartOpen);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('pageshow', this.handlePageShow);
  }

  handleDeliveryTypeChange(event) {
    if (event.target.matches('input[name="delivery-type"]')) {
      const value = event.target.value;
      const daycareSelector = document.getElementById('daycare-selector');

      if (value === 'daycare') {
        daycareSelector?.classList.add('active');
        this.displayNextDeliveryDate();
      } else {
        daycareSelector?.classList.remove('active');
        // Update cart attributes immediately when standard is selected
        if (value === 'standard') {
          this.updateCartAttributes('standard', '');
        }
      }
      const checkoutButton = document.querySelector('[name="checkout"]');
      if (checkoutButton) {
        const hasSelection = document.querySelector('input[name="delivery-type"]:checked');
        checkoutButton.disabled = !hasSelection;
      }
    }
  }

  async handleCheckoutClick(event) {
    const selectedDeliveryType = document.querySelector('input[name="delivery-type"]:checked')?.value;
    const selectedDaycare = document.querySelector('#daycare-selector input[type="text"]')?.value.trim() || '';

    if (selectedDeliveryType === 'daycare') {
      const validDaycare = this.daycareList.find(d => d.toLowerCase() === selectedDaycare.toLowerCase());

      if (validDaycare) {
        event.preventDefault();
        try {
          const nextDeliveryDateSpan = document.getElementById('next-delivery-date');
          const nextDeliveryDate = nextDeliveryDateSpan?.textContent || '';
          await this.updateCartAttributes(validDaycare, nextDeliveryDate);
          window.location.href = '/checkout';
        } catch (error) {
          window.location.href = '/checkout';
        }
      } else {
        event.preventDefault();
        alert(`${theme.settings.daycare_delivery_select_valid_option}`);
      }
    } else if (selectedDeliveryType === 'standard') {
      console.log('Standard delivery selected, proceeding to checkout.');
    }
  }

  handleDaycareInput(event) {
    if (event.target.matches('#daycare-selector input[type="text"]')) {
      const query = event.target.value.trim().toLowerCase();
      const suggestionsList = document.getElementById('suggestions');

      if (!suggestionsList) return;

      if (query.length < 3) {
        suggestionsList.innerHTML = '';
        return;
      }

      const filtered = this.daycareList.filter(item =>
        item.toLowerCase().includes(query)
      );

      suggestionsList.innerHTML = '';
      filtered.forEach((suggestion) => {
        const li = document.createElement('li');
        li.textContent = suggestion;
        li.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          const daycareSelectorInput = document.querySelector('#daycare-selector input[type="text"]');
          if (daycareSelectorInput) {
            daycareSelectorInput.value = suggestion;
            suggestionsList.innerHTML = '';
          }
        });
        suggestionsList.appendChild(li);
      });
    }
  }

  handleSuggestionClick(event) {
    // Handled in handleDaycareInput method
  }

  handleCartUpdate() {
    setTimeout(async () => {
      await this.checkCartAttributes();
      this.setupCheckoutButtonListener();
    }, 500);
  }

  handleCartOpen() {
    setTimeout(() => {
      this.init();
    }, 100);
  }

  handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
      this.init();
    }
  }

  handlePageShow(event) {
    if (event.persisted) {
      this.init();
    }
  }

  setupCheckoutButtonListener() {
    const checkoutButton = document.querySelector('[name="checkout"]');
    if (checkoutButton && !checkoutButton.dataset.daycareListenerAdded) {
      checkoutButton.addEventListener('click', this.handleCheckoutClick, { capture: true });
      checkoutButton.dataset.daycareListenerAdded = 'true';
    }
  }

  displayNextDeliveryDate() {
    const today = new Date();
    const currentDay = today.getDay();
    const daysUntilMonday = currentDay === 1 ? 7 : (8 - currentDay) % 7;

    const nextMonday = new Date(today);
    nextMonday.setDate(today.getDate() + daysUntilMonday);

    const referenceMonday = new Date(2024, 4, 20); // May 20, 2024
    const daysBetween = Math.round((nextMonday - referenceMonday) / (24 * 60 * 60 * 1000));

    if (daysBetween % 14 !== 0) {
      nextMonday.setDate(nextMonday.getDate() + 7);
    }

    const options = { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' };
    const formatted = nextMonday.toLocaleDateString(
      (typeof Shopify !== 'undefined' && Shopify.locale) || 'en-US', 
      options
    );

    const nextDeliveryDateSpan = document.getElementById('next-delivery-date');
    if (nextDeliveryDateSpan) {
      nextDeliveryDateSpan.textContent = formatted;
    }
  }

  async loadDaycareList() {
    try {
      this.daycareList = await this.fetchAllDaycares();
    } catch (error) {
      console.error('Error loading daycare list:', error);
    }
  }

  async fetchAllDaycares(cursor = null, collectedDaycares = []) {
    try {
      const response = await fetch(this.STOREFRONT_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.STOREFRONT_API_TOKEN,
        },
        body: JSON.stringify({
          query: `
            {
              metaobjects(first: 100, type: "daycare"${cursor ? `, after: "${cursor}"` : ''}) {
                edges {
                  node {
                    handle
                    fields {
                      key
                      value
                    }
                  }
                  cursor
                }
                pageInfo {
                  hasNextPage
                }
              }
            }
          `,
        }),
      });

      const result = await response.json();
      
      if (!result.data) {
        console.error('Invalid API response:', result);
        return collectedDaycares;
      }

      const edges = result.data.metaobjects.edges;
      const daycares = edges.map(edge => {
        const titleField = edge.node.fields.find(f => f.key === 'name');
        return titleField?.value || '';
      }).filter(Boolean);

      const allDaycares = [...collectedDaycares, ...daycares];
      
      // Check if there are more pages to fetch
      if (result.data.metaobjects.pageInfo.hasNextPage && edges.length > 0) {
        const lastCursor = edges[edges.length - 1].cursor;
        // Recursively fetch next page
        return this.fetchAllDaycares(lastCursor, allDaycares);
      }
      
      return allDaycares;
    } catch (error) {
      console.error('Error fetching daycares:', error);
      return collectedDaycares;
    }
  }

  async checkCartAttributes() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      if (cart.attributes) {
        const daycareName = cart.attributes['daycare_delivery'];
        // Updated this line to properly handle when daycareName is 'standard'
        const deliveryType = daycareName === 'standard' ? 'standard' : (daycareName ? 'daycare' : 'standard');

        console.log('Cart attributes:', cart.attributes);

        // If valid delivery type exists, select it
        if (deliveryType) {
          const radioToSelect = document.querySelector(`input[name="delivery-type"][value="${deliveryType}"]`);
          if (radioToSelect) {
            radioToSelect.checked = true;
          }
        }

        // Update UI based on selection
        if (deliveryType === 'daycare' && daycareName && daycareName !== 'standard') {
          const daycareInput = document.querySelector('#daycare-selector input[type="text"]');
          if (daycareInput && daycareName) {
            daycareInput.value = daycareName;
          }

          const daycareSelector = document.getElementById('daycare-selector');
          if (daycareSelector) {
            daycareSelector.classList.add('active');
          }

          this.displayNextDeliveryDate();
        } else {
          const daycareSelector = document.getElementById('daycare-selector');
          if (daycareSelector) {
            daycareSelector.classList.remove('active');
          }
        }
        
        const checkoutButton = document.querySelector('[name="checkout"]');
        if (checkoutButton) {
          const hasSelection = document.querySelector('input[name="delivery-type"]:checked');
          checkoutButton.disabled = !hasSelection;
        }

        // Manually dispatch change event if a radio button was selected programmatically
        const selectedRadio = document.querySelector('input[name="delivery-type"]:checked');
        if (selectedRadio) {
          selectedRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    } catch (error) {
      console.error('Error fetching cart attributes:', error);
    }
  }

  async init() {
    if (this.isInitialized) return;
    
    await this.loadDaycareList();
    await this.checkCartAttributes();
    this.setupCheckoutButtonListener();
    
    this.isInitialized = true;
  }

  // Public methods
  getSelectedDeliveryType() {
    return document.querySelector('input[name="delivery-type"]:checked')?.value;
  }

  getSelectedDaycare() {
    return document.querySelector('#daycare-selector input[type="text"]')?.value.trim() || '';
  }

  getDaycareList() {
    return [...this.daycareList];
  }

  async refreshDaycareList() {
    await this.loadDaycareList();
  }

  disableCheckoutIfNoSelection() {
    const checkoutButton = document.querySelector('[name="checkout"]');
    if (checkoutButton) {
      const hasSelection = document.querySelector('input[name="delivery-type"]:checked');
      checkoutButton.disabled = !hasSelection;
    }
  }

  async updateCartAttributes(daycareValue, nextDeliveryDate = '') {
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'        },
        body: JSON.stringify({
          attributes: {
            'delivery_type': daycareValue === 'standard' ? 'standard' : 'daycare',
            'daycare_delivery': daycareValue,
            'next_delivery_date': nextDeliveryDate
          }
        })
      });
    } catch (error) {
      console.error('Error updating cart attributes:', error);
    }
  }
}

// Register the custom element
customElements.define('daycare-delivery', DaycareDeliveryComponent);

// Export for use in modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = DaycareDeliveryComponent;
}